import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { graphql, useStaticQuery } from 'gatsby';
import { ArrayParam, NumberParam, useQueryParam } from 'use-query-params';
import { useQuery } from 'react-query';
import { JsonLd } from 'react-schemaorg';
import { ItemList } from 'schema-dts';

import BuyingHero from './index';
import { SearchGridResultsMapper } from '../SearchGrid';
import Pagination from '../Pagination';
import { ProductCardProps } from '../ProductCard';
import Filters from './Filters';

import { PaginationProvider } from '../../utils/ctx/PaginationContext';
import { getSalesWatches, SortingTypes } from '../../utils/api';
import generateWatchProjectImage from '../../utils/generateWatchProjectImage';
import { BUYING_CATALOG_ROUTE, BUYING_PRODUCTS_ROUTE } from '../../utils/routes/const';
import { I18NextContext } from 'gatsby-plugin-react-i18next/dist/types';
import Link from '../Atoms/Link';

const SEARCH_RESULTS_PER_PAGE = 32;

type BuyingHeroFilterProps = {
  bgColorHero?: string,
  title?: string,
  description?: string,
  brand?: string,
  model?: string,
}

const filtersQuery = graphql`
query {
  movements: allSalesWatch {
    distinct(field: {movement: SELECT})
  }
  genders: allSalesWatch {
    distinct(field: {gender: SELECT})
  }
  watchConditions: allSalesWatch {
    distinct(field: {watchCondition: SELECT})
  }
  brands: allSalesWatch {
    distinct(field: {watch: {brand: {name: SELECT}}})
    nodes {
      watch {
        brand {
          name
        }
      }
    }
  }
  models: allSalesWatch {
    distinct(field: {watch: {model: {name: SELECT}}})
  }
}
`

const resultMappingFunction = (localeContext: I18NextContext, result: WatchProject21Api.SalesWatch) => {
  const { watch } = result;

  const productCardResults: ProductCardProps = {
    title: `${watch.brand ? watch.brand.name : ''} ${watch.model ? watch.model.name : ''}${result.additionalTitle ? ` - ${result.additionalTitle}` : ''} `,
    price: !result.price ? result.priceOnDemand : Intl.NumberFormat(localeContext.language, { style: 'currency', currency: 'EUR', currencyDisplay: 'symbol' }).format(result.price || 0),
    image: generateWatchProjectImage(result.imagePath, watch.referenceNumber),
    link: BUYING_PRODUCTS_ROUTE(result.slug.replace(/^\/|\/$/g, '')),
    border: true,
    type: 'buy',
    imageWrapperClass: 'px-4 pt-4',
    imageWrapperSubClass: 'border border-3 border-gray',
    textWrapperClass: 'px-4 pb-4 !pt-0',
  };

  return productCardResults;
}

const translateFilterOptions = (options, t: (v: string) => string) => {
  const { movements, genders, watchConditions, brands, models } = options;
  const translateByKey = (key: string, value: string) => t(`${key}.${value}`);

  const movementsTranslated = movements.distinct.map((movement: string) => ({ id: movement, name: translateByKey('general.movement', movement) }))
  const gendersTranslated = genders.distinct.map((gender: string) => ({ id: gender, name: translateByKey('general.gender', gender) }))
  const watchConditionsTranslated = watchConditions.distinct.map((watchCondition: string) => ({ id: watchCondition, name: translateByKey('general.watchCondition', watchCondition) }))

  return {
    movements: movementsTranslated,
    genders: gendersTranslated,
    watchConditions: watchConditionsTranslated,
    brands,
    models
  }
}

const BuyingHeroFilter = ({ bgColorHero = 'bg-blue', title = 'buyingHero.title', description = '', brand = '', model = '' }: BuyingHeroFilterProps) => {
  const { t } = useTranslation();
  const paginationTopScrollRef = useRef(null);
  const { movements, genders, watchConditions, brands, models } = translateFilterOptions(useStaticQuery(filtersQuery), t);
  const [filterBrands = [brand].filter(Boolean) || [], setBrands] = useQueryParam('brandName', ArrayParam);
  const [filterConditions = [], setCondition] = useQueryParam('watchCondition', ArrayParam);
  const [filterMovements = [], setMovement] = useQueryParam('movement', ArrayParam);
  const [filterGenders = [], setGender] = useQueryParam('gender', ArrayParam);
  const [filterModels = [model].filter(Boolean), setModels] = useQueryParam('modelName', ArrayParam);
  const [page = 1, setPage] = useQueryParam('page', NumberParam);
  const [sorting, setSorting] = useState<SortingTypes>('createdAt_desc');

  const filters = useMemo(() => ({
    brandName: filterBrands,
    watchCondition: filterConditions,
    movement: filterMovements,
    gender: filterGenders,
    modelName: filterModels,
    limit: SEARCH_RESULTS_PER_PAGE,
    page,
  }), [filterBrands, filterConditions, filterMovements, filterGenders, filterModels, page])

  const { data, isLoading } = useQuery(`sales-watches/${page}-${filters ? Object.entries(filters).join("-") : ""}-${sorting}`, () => getSalesWatches(filters, sorting))

  const mappedData = useMemo(() => {
    if (data) {
      console.log(data['hydra:member']);
      data['hydra:member'] = data['hydra:member'].map((result: WatchProject21Api.SalesWatch) => {
        const priceOnDemand: string = t('general.priceOnDemand');

        result['priceOnDemand'] = priceOnDemand;

        return result;
      })

      return data;
    }
    return data;
  }, [data, t])

  const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    filterBrands?.includes(e.target.id) ? setBrands(filterBrands?.filter(f => f !== e.target.id)) : setBrands([...filterBrands, e.target.id]);

  const handleConditionChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    filterConditions?.includes(e.target.id) ? setCondition(filterConditions?.filter(f => f !== e.target.id)) : setCondition([...filterConditions, e.target.id]);

  const handleGenderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filterGenders?.includes(e.target.id) ? setGender(filterGenders?.filter(f => f !== e.target.id)) : setGender([...filterGenders, e.target.id]);
  }

  const handleMovementChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    filterMovements?.includes(e.target.id) ? setMovement(filterMovements?.filter(f => f !== e.target.id)) : setMovement([...filterMovements, e.target.id]);

  const handleModelChange = (selectedModels) =>
    setModels(selectedModels.map(model => model.value));

  const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => setSorting(e.target.value);

  return (
    <>
      <BuyingHero bgColor={bgColorHero} title={title} description={description} brand={brand} />
      <section className={`mx-auto ${bgColorHero}`}>
        <div className="max-w-screen-2xl mx-auto">
          <h2 className="font-cirka text-xl text-center mb-8">{t('buy.catalog.explore')}</h2>
          {(model || brand) ? (
            <Link to={BUYING_CATALOG_ROUTE()} className="font-bold text-lg flex w-full px-5 pt-4 pb-6 hover:underline">{t('buy.catalog.back')}</Link>
          ) : null}
          <Filters
            paginationTopScrollRef={paginationTopScrollRef}
            handleOrderChange={handleOrderChange}
            brand={brand}
            brands={brands}
            filterBrands={filterBrands}
            handleBrandChange={handleBrandChange}
            watchConditions={watchConditions}
            filterConditions={filterConditions}
            handleConditionChange={handleConditionChange}
            genders={genders}
            filterGenders={filterGenders}
            handleGenderChange={handleGenderChange}
            movements={movements}
            filterMovements={filterMovements}
            handleMovementChange={handleMovementChange}
            models={models}
            filterModels={filterModels}
            handleModelChange={handleModelChange}
          />
          <JsonLd<ItemList>
            item={{
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: mappedData && mappedData['hydra:member'].map((result, index) => ({
                '@type': 'ListItem',
                position: index,
                item: {
                  '@type': 'Product',
                  name: `${result.watch.brand ? result.watch.brand.name : ''} ${result.watch.model ? result.watch.model.name : ''} (${result.watch.referenceNumber})`,
                  image: generateWatchProjectImage(result.imagePath, result.watch.referenceNumber),
                  description: result.watch.description,
                  sku: result.watch.referenceNumber,
                  mpn: result.watch.referenceNumber,
                  brand: {
                    '@type': 'Brand',
                    name: result.watch.brand ? result.watch.brand.name : '',
                  },
                  offers: {
                    '@type': 'Offer',
                    priceCurrency: 'EUR',
                    price: result.priceOnDemand,
                    priceValidUntil: result.endDate,
                    itemCondition: 'https://schema.org/NewCondition',
                    availability: 'https://schema.org/InStock',
                    url: BUYING_PRODUCTS_ROUTE(result.slug.replace(/^\/|\/$/g, '')),
                  },
                },
                url: BUYING_PRODUCTS_ROUTE(result.slug.replace(/^\/|\/$/g, '')),
              })),
            }}
          />
          <SearchGridResultsMapper gridClasses='grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2 gap-4 p-5' query="query" data={mappedData} isLoading={isLoading} resultMappingFunction={resultMappingFunction} />
          <div className="mx-20 py-8">
            <PaginationProvider currentPage={page || 1} setPage={setPage} scrollRef={paginationTopScrollRef}>
              <Pagination maxPages={data && Math.ceil(data['hydra:totalItems'] / SEARCH_RESULTS_PER_PAGE)} />
            </PaginationProvider>
          </div>
        </div>
      </section>
    </>
  );
}

export default BuyingHeroFilter;

